import React, { useEffect, useState } from 'react'
import { Box, Grid, Heading, Paragraph } from 'grommet'
import { useMyDataset } from 'hooks/useMyDataset'
import { useProjectSamplesTable } from 'hooks/useProjectSamplesTable'
import { useResponsive } from 'hooks/useResponsive'
import { getProjectFormats } from 'helpers/getProjectFormats'
import { differenceArray } from 'helpers/differenceArray'
import { pluralize } from 'helpers/pluralize'
import { Button } from 'components/Button'
import { Modal, ModalBody } from 'components/Modal'
import { DatasetDataFormatOptions } from 'components/DatasetDataFormatOptions'
import { DatasetSamplesProjectOptions } from 'components/DatasetSamplesProjectOptions'
import { WarningAnnDataMultiplexed } from 'components/WarningAnnDataMultiplexed'

export const DatasetAddSamplesModal = ({
  project,
  samples,
  label = 'Add Samples to Dataset',
  title = 'Add Samples to Dataset',
  disabled = false
}) => {
  const {
    myDataset,
    getMyDatasetProjectDataSamples,
    getMyDatasetProjectIsMerged,
    setMyDatasetSamples,
    userFormat,
    setUserFormat
  } = useMyDataset()
  const {
    canAddMultiplexed,
    showWarningMultiplexed,
    selectedSamples,
    setSelectedSamples
  } = useProjectSamplesTable()
  const { responsive } = useResponsive()

  const [selectedSingleCellSamples, setSelectedSingleCellSamples] = useState([])
  const [selectedMultiplexedSamples, setSelectedMultiplexedSamples] = useState(
    []
  )
  // Exclude multiplexed samples from selectedSamples for ANN_DATA
  // NOTE: Make sure not to lose the user's selection when toggling formats before API request
  useEffect(() => {
    if (!samples) return

    if (!project.has_multiplexed_data || canAddMultiplexed) {
      setSelectedSingleCellSamples(selectedSamples.SINGLE_CELL)
      return
    }

    const selectedSet = new Set(selectedSamples.SINGLE_CELL)
    setSelectedSingleCellSamples(
      samples
        .filter((s) => !s.has_multiplexed_data && selectedSet.has(s.scpca_id))
        .map((s) => s.scpca_id)
    )
  }, [userFormat, canAddMultiplexed, selectedSamples])

  // Get multiplexed samples in selectedSamples
  useEffect(() => {
    if (!samples || !project.has_multiplexed_data) return

    const samplesSet = new Set(selectedSamples.SINGLE_CELL)
    setSelectedMultiplexedSamples(
      samples
        .filter((s) => s.has_multiplexed_data && samplesSet.has(s.scpca_id))
        .map((s) => s.scpca_id)
    )
  }, [userFormat, canAddMultiplexed, selectedSamples])

  // Modal toggle
  const [showing, setShowing] = useState(false)
  const [loading, setLoading] = useState(false)

  // For project options
  const [includeBulk, setIncludeBulk] = useState(false)

  // For counts of to-be-added samples in the modal
  const [singleCellSamplesToAdd, setSingleCellSamplesToAdd] = useState([])
  const [spatialSamplesToAdd, setSpatialSamplesToAdd] = useState([])

  // For the modal UI
  const totalSamples = singleCellSamplesToAdd + spatialSamplesToAdd
  const canClickAddSamples =
    userFormat === 'ANN_DATA'
      ? totalSamples - selectedMultiplexedSamples.length > 0
      : totalSamples > 0
  const isDisabled = disabled || !canClickAddSamples

  const handleAddSamples = async () => {
    setLoading(true)
    // Ensure that the merged object is retained if present in myDataset
    const samplesToAdd = getMyDatasetProjectIsMerged(project)
      ? 'MERGED'
      : selectedSingleCellSamples

    await setMyDatasetSamples(
      project,
      {
        ...selectedSamples,
        SINGLE_CELL: samplesToAdd,
        includes_bulk: includeBulk
      },
      userFormat
    )
    setShowing(false)
    // Refresh the table data
    setSelectedSamples((prev) => ({
      ...prev,
      SINGLE_CELL: selectedSingleCellSamples
    }))
    setLoading(false)
  }

  // Reset Data Format dropdown value on modal closes
  useEffect(() => {
    if (!myDataset.format) {
      setUserFormat(getProjectFormats(project)[0])
    } else {
      setUserFormat(myDataset.format)
    }
  }, [myDataset.format, showing])

  // Calculate to-be-added samples for each modality
  useEffect(() => {
    if (samples) {
      const { SINGLE_CELL: singleCellSamples, SPATIAL: spatialSamples } =
        getMyDatasetProjectDataSamples(project)

      setSingleCellSamplesToAdd(
        differenceArray(selectedSamples.SINGLE_CELL, singleCellSamples)
          .length || 0
      )
      setSpatialSamplesToAdd(
        differenceArray(selectedSamples.SPATIAL, spatialSamples).length || 0
      )
    }
  }, [
    userFormat,
    canAddMultiplexed,
    selectedSingleCellSamples,
    samples,
    selectedSamples
  ])

  useEffect(() => {
    setLoading(false)
  }, [showing])

  return (
    <>
      <Button
        aria-label={label}
        flex="grow"
        primary
        label={label}
        disabled={isDisabled}
        onClick={() => setShowing(true)}
      />
      <Modal title={title} showing={showing} setShowing={setShowing}>
        <ModalBody>
          <Grid
            columns={['auto']}
            margin={{ bottom: '0' }}
            pad={{ bottom: '0' }}
          >
            <Box margin={{ bottom: 'medium' }}>
              <Paragraph margin={{ bottom: 'xsmall' }}>
                You have selected the following to add to My Dataset:
              </Paragraph>
              <Paragraph>
                {pluralize(`${totalSamples} sample`, totalSamples)}
              </Paragraph>
              <Box
                as="ul"
                margin={{ top: '0' }}
                pad={{ left: '26px' }}
                style={{ listStyle: 'disc' }}
              >
                <Box as="li" style={{ display: 'list-item' }}>
                  {`${pluralize(
                    `${singleCellSamplesToAdd} sample`,
                    singleCellSamplesToAdd
                  )} with single-cell modality`}
                </Box>

                {project.has_spatial_data && (
                  <Box as="li" style={{ display: 'list-item' }}>
                    {`${pluralize(
                      `${spatialSamplesToAdd} sample`,
                      spatialSamplesToAdd
                    )} with spatial modality`}
                  </Box>
                )}
              </Box>
            </Box>
            <Heading level="3" size="small" margin={{ bottom: 'medium' }}>
              Additional Download Options
            </Heading>
            <Box pad={{ top: 'small' }}>
              <Box pad={{ bottom: 'medium' }} width="680px">
                <Box margin={{ bottom: 'medium' }}>
                  <DatasetDataFormatOptions project={project} />
                  {showWarningMultiplexed && (
                    <WarningAnnDataMultiplexed
                      count={selectedMultiplexedSamples.length}
                    />
                  )}
                </Box>
                <DatasetSamplesProjectOptions
                  project={project}
                  includeBulk={includeBulk}
                  onIncludeBulkChange={setIncludeBulk}
                />
              </Box>
              <Box
                align="center"
                direction={responsive('column', 'row')}
                gap="xlarge"
              >
                <Button
                  primary
                  aria-label={label}
                  label={label}
                  disabled={isDisabled}
                  onClick={handleAddSamples}
                  loading={loading}
                />
              </Box>
            </Box>
          </Grid>
        </ModalBody>
      </Modal>
    </>
  )
}
